import type { ErrorResponse } from './errorHandler';

/**
 * Mensagens amigáveis por código retornado pelo backend (ex.: `notifications[].key`).
 * Manter alinhado com os códigos da API.
 */
export const BACKEND_ERROR_MESSAGES: Record<string, ErrorResponse> = {
  PAGAR_ME_CREATE_CARD_ERROR: {
    title: 'Pagamento',
    message: `Não foi possível processar o pagamento com seu cartão. Isso pode acontecer por alguns motivos simples — verifique os pontos abaixo antes de tentar novamente:
* Digite o nome exatamente como aparece no cartão físico
* Confira se a data de validade está correta
* Preencha os dados manualmente, sem usar o preenchimento automático do navegador
* Se o problema persistir, tente utilizar um cartão virtual — alguns cartões físicos possuem restrições para compras online

Caso o problema continue entre em contato com seu banco ou emissor do cartão.`,
    type: 'error'
  }
};

export function resolveBackendErrorResponse(errorKey: string): ErrorResponse | null {
  const key = errorKey.trim();
  if (!key) return null;
  return BACKEND_ERROR_MESSAGES[key] ?? null;
}

/**
 * Converte uma notificação da API (`key` + `message`) no payload do toast.
 * Se existir mapeamento para `key`, usa título e texto personalizados; caso contrário, usa a mensagem da API.
 */
export function toastFromApiNotification(notification: { key?: string; message?: string }): ErrorResponse {
  if (notification.key) {
    const resolved = resolveBackendErrorResponse(notification.key);
    if (resolved) return resolved;
  }
  return {
    title: 'Erro',
    message: notification.message ?? 'Ocorreu um erro inesperado.',
    type: 'error'
  };
}

/**
 * Se o erro Axios tiver `notifications[0].key` em `BACKEND_ERROR_MESSAGES`, devolve esse toast.
 * Usado em `getErrorMessage` antes de `customMessage`, para códigos como PAGAR_ME_CREATE_CARD_ERROR.
 */
export function tryGetMappedNotificationToast(error: unknown): ErrorResponse | null {
  if (!error || typeof error !== 'object' || !(error as { isAxiosError?: boolean }).isAxiosError) {
    return null;
  }
  const data = (error as { response?: { data?: { notifications?: unknown[] } } }).response?.data;
  const notifications = data?.notifications;
  if (!Array.isArray(notifications) || notifications.length === 0) {
    return null;
  }
  const first = notifications[0] as { key?: string; message?: string };
  if (!first || typeof first !== 'object') {
    return null;
  }
  const key = typeof first.key === 'string' ? first.key : undefined;
  if (!key || !resolveBackendErrorResponse(key)) {
    return null;
  }
  return toastFromApiNotification({
    key,
    message: typeof first.message === 'string' ? first.message : undefined
  });
}

export const KNOWN_BACKEND_ERROR_IDS = Object.keys(BACKEND_ERROR_MESSAGES);
